import {BaseElementAsync, MsgLabel, OSkinableInit} from "back/commons/basis";
import {IApp, IAppCtx} from "back/core/appFrame";
import {JSX} from "lib/commons/xml/dom";
import {IReg, IRegPointer, REG} from "lib/commons/registry";
import {DOMSH} from "lib/commons/xml/domsh";
import {IDepotEnv, IDepotUiEnv} from "lib/store/depot";
import {JDepotDeskAppDef} from "back/store/plugins/depotPlg";
import {IInfo, IInfoConsumer, InfoBrokerBasic} from "lib/commons/infos";
import {InfoCurrentRes, InfoFocusRes} from "lib/store/res";
import {IArea} from "lib/commons/areas";
import {IView, IViewContainer, OViewVisitOptions, VIEWS} from "lib/commons/views";
import {JLDTabs, Tabs} from "back/commons/widgets/tabs";
import {JLastDatas} from "lib/commons/lastDatas";
import "back/core/zones/webZones";
import {EWsState} from "lib/core/universe";

/**
 * App de dépot de type Desk.
 *
 */
export interface DepotDeskApp extends BaseElementAsync {
	initialize(init: ODepotDeskAppInit): this;
}

export interface ODepotDeskAppInit extends IAppCtx<IDepotUiEnv>, OSkinableInit {
	reg?: IReg<IDepotEnv>

	/** Onglets à afficher. */
	boards: IArea<IRegPointer<IDepotUiEnv>>[]

	lastDatas: JLDDepotDeskApp
}

export class DepotDeskApp extends BaseElementAsync implements IApp<IDepotUiEnv>, IInfoConsumer, IViewContainer {

	appDef: JDepotDeskAppDef;

	reg: IReg<IDepotUiEnv>;

	view: IView;

	protected async _initialize(init: ODepotDeskAppInit) {
		this.appDef = init.appDef as JDepotDeskAppDef;
		const universe = init.reg.env.universe;
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);

		this.setAttribute("label", universe.getName());

		if (!universe.auth.currentUser) {
			sr.appendChild(new MsgLabel().setStandardMsg('needAuth'));
			return; //throw Error("Auth required");
		}
		this.reg = universe.newDepotUiReg(this);
		this.reg.env.infoBroker = new InfoBrokerBasic();
		this.reg.env.infoBroker.addConsumer(this);

		//TODO Check perm pour cette ihm.
		// if (!this.reg.hasPerm("ui.depotDeskApp")) {
		// 	sr.appendChild(new MsgLabel().setStandardMsg('accessDenied'));
		// 	return;
		// }

		if (universe.wsFrames?.ws.wsState !== EWsState.wsOpened) await universe.wsFrames?.ws.startWs();

		if (init.boards.length > 1) {
			if (this.appDef.dptPath != null) {
				//TODO trouver le 1er board qui peut afficher une ressource : impl IResViewable ?
			}
			const v = this.view = new Tabs<IRegPointer<IDepotUiEnv>>().initialize({
				reg: this.reg,
				areas: init.boards,
				areasContext: this,
				skinOver: 'store-depotdesk-app/tabs',
				lastDatas: init.lastDatas?.board,
				lastDatasKey: "board"
			});
			await v.initializedAsync;
			sr.appendChild(v);
		} else {
			this.view = sr.appendChild(await init.boards[0].loadBody(this, init.lastDatas));
		}
		VIEWS.onViewShown(this.view); //FIXME est onViewShown() appelé par le Desk. Revoir logique d'init et synchro de l'appDef entre les != boards.
		this.onAppDefChange();
	}

	updateAppDef(def: JDepotDeskAppDef): boolean {
		if (this.appDef === def) return true; //chgt issu d'ici
		this.appDef = def;
		if (this.initialized) {
			this.onAppDefChange();
		} else {
			this.initializedAsync.then(() => this.onAppDefChange());
		}
		return true;
	}

	onViewHidden(closed?: boolean) {
		try {
			VIEWS.onContainerHidden(this, closed);
		} finally {
			if (closed) this.reg?.close();
		}
	}

	protected onAppDefChange() {
		//TODO switch board (browse/search...)
		this.reg.env.infoBroker.dispatchInfo(new InfoFocusRes(this.appDef.dptPath), this);
	}

	onInfo(info: IInfo): void {
		if (this.initialized) {
			if (info instanceof InfoCurrentRes) {
				if (info.resPath != this.appDef.dptPath) {
					this.appDef.dptPath = info.resPath;
					this.dispatchEvent(new CustomEvent('c-appdef-change', {bubbles: true, detail: {revertable: true}}));
				}
				//this._refreshTitle(shortDesc ? ITEM.getSrcMainName(this.reg, shortDesc, this.wsp.wspMetaUi) : null);
			}
		} else {
			this.initializedAsync.then(() => this.onInfo(info));
		}
	}

	visitViews(visitor: (view: IView) => any): any {
		if (this.view) return visitor(this.view);
	}

	visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		return this.view ? visitor(this.view) : Promise.resolve(undefined);
	}
}


export interface JLDDepotDeskApp extends JLastDatas {
	board?: JLDTabs
}

REG.reg.registerSkin('store-depotdesk-app', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: row;
	}

	c-tabs {
		flex: 1;
	}
`);

REG.reg.registerSkin('store-depotdesk-app/tabs', 1, /* language=CSS */ `
	c-tab {
		padding: .7em !important;
	}

	#panels {
		border: none;
		border-top: solid 1px var(--border-color);
	}
`);
customElements.define('store-depotdesk-app', DepotDeskApp);
